import type { AdminStatsDto, AuditLogDto } from '@bravo/shared';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, ErrorBlock, PageHeader, Spinner } from '../components/ui';
import { apiData, apiPage } from '../lib/api';
import { useFormat } from '../lib/format';

export function DashboardPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const stats = useQuery({
    queryKey: ['stats'],
    queryFn: () => apiData<AdminStatsDto>('/admin/stats'),
  });
  const audit = useQuery({
    queryKey: ['audit', 'latest'],
    queryFn: () => apiPage<AuditLogDto>('/admin/audit', { limit: 6 }),
  });

  if (stats.isLoading) return <Spinner />;
  if (stats.isError || !stats.data) {
    return <ErrorBlock message={t('errorTitle')} onRetry={() => void stats.refetch()} />;
  }
  const data = stats.data;
  const cards = [
    { label: t('dash.users'), value: String(data.users) },
    { label: t('dash.openMarkets'), value: String(data.openMarkets) },
    { label: t('dash.awaiting'), value: String(data.marketsAwaitingResolution) },
    { label: t('dash.pendingTopups'), value: String(data.pendingTopUps), href: '/topups' },
    { label: t('dash.secondApproval'), value: String(data.topUpsAwaitingSecondApproval) },
    { label: t('dash.pendingWithdrawals'), value: String(data.pendingWithdrawals), href: '/withdrawals' },
    { label: t('dash.processing'), value: String(data.processingWithdrawals) },
    { label: t('dash.stakes24h'), value: `${format.etb(data.stakes24h.amount)} · ${data.stakes24h.count}` },
    { label: t('dash.revenue'), value: format.etb(data.platformRevenue) },
    {
      label: t('dash.liabilities'),
      value: `${format.etb(data.liabilities.available)} + ${format.etb(data.liabilities.held)}`,
    },
  ];

  return (
    <div>
      <PageHeader title={t('dash.title')} subtitle={t('dash.subtitle')} />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {cards.map((card) => {
          const inner = (
            <Card key={card.label} className="h-full">
              <p className="text-xs font-medium uppercase tracking-wide text-fg-muted">{card.label}</p>
              <p className="mt-2 text-2xl font-semibold tracking-tight">{card.value}</p>
            </Card>
          );
          return card.href ? (
            <Link key={card.label} to={card.href} className="block">
              {inner}
            </Link>
          ) : (
            <div key={card.label}>{inner}</div>
          );
        })}
      </div>
      <h2 className="mb-3 mt-8 text-lg font-semibold">{t('dash.recentAudit')}</h2>
      {audit.isLoading ? <Spinner /> : null}
      {audit.data && audit.data.items.length === 0 ? (
        <p className="text-sm text-fg-muted">{t('empty')}</p>
      ) : null}
      <ul className="divide-y divide-border rounded-2xl border border-border bg-card">
        {audit.data?.items.map((event) => (
          <li key={event.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
            <span className="font-medium">{event.action}</span>
            <span className="text-fg-muted">{event.actorName || event.actorType}</span>
            <span className="text-fg-muted">{format.when(event.createdAt)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
